import React, { useEffect, useState } from 'react'
import { DeleteOutlined, MenuOutlined, PlusOutlined } from '@ant-design/icons'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import styled from '@emotion/styled'
import { Button, Input } from 'antd'

interface InputListProps {
  onChange: (value: string[]) => void,
  value: string[],
  allowEmptyValues?: boolean
}

interface ListItem {
  id: string,
  value: string
}

/**
 * Input list component for managing an array of string inputs. You can add, remove, edit, and reorder items in the list.
 *
 * @param props
 * @param props.onChange Callback function that is called when the list changes. Receives the updated array of strings.
 * @param props.value The current array of strings to display in the list.
 * @param props.validateItems Whether to validate individual items for empty values.
 * @returns {JSX.Element}
 */
export default function InputList({ onChange, value, allowEmptyValues = true }: InputListProps) {
  // Generate stable IDs for items that persist during editing but regenerate when array structure changes
  // Because value doesn't have IDs, we need to manage them here
  // IDs help with drag-and-drop animation and preserving input state during edits
  const [itemsWithIds, setItemsWithIds] = useState<ListItem[]>([])

  useEffect(function syncIdsOnChange() {
    setItemsWithIds((prev) => {
      // If lengths are different, regenerate all IDs
      if (prev.length !== value.length) {
        return value.map((val) => ({
          id: crypto.randomUUID(),
          value: val
        }))
      }

      // If lengths are same, check if values have changed in a way that suggests reordering
      const prevValues = prev.map((item) => item.value)
      const valuesChanged = !value.every((val, index) => val === prevValues[index])

      if (valuesChanged) {
        // Check if this is a reorder (same items, different positions)
        const sortedPrev = [...prevValues].sort()
        const sortedCurrent = [...value].sort()
        const isReorder = sortedPrev.length === sortedCurrent.length &&
                         sortedPrev.every((val, index) => val === sortedCurrent[index])

        if (isReorder) {
          return prev
        }
      }

      return prev.map((item, index) => ({
        ...item,
        value: value[index]
      }))
    })
  }, [value])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )
  const handleAdd = () => {
    onChange([...value, ''])
  }

  const handleRemove = (index: number) => {
    const newValue = value.filter((_, i) => i !== index)
    onChange(newValue)
  }

  const handleChange = (index: number, newValue: string) => {
    const updated = [...value]
    updated[index] = newValue
    onChange(updated)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = itemsWithIds.findIndex((item) => item.id === active.id)
      const newIndex = itemsWithIds.findIndex((item) => item.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(itemsWithIds, oldIndex, newIndex)
        setItemsWithIds(reorderedItems)

        onChange(reorderedItems.map((item) => item.value))
      }
    }
  }

  return (
    <Wrapper>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemsWithIds.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {itemsWithIds.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              value={item.value}
              index={index}
              onChange={(newValue) => handleChange(index, newValue)}
              onRemove={() => handleRemove(index)}
              allowEmptyValues={allowEmptyValues}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        type={'dashed'}
        onClick={handleAdd}
        icon={<PlusOutlined />}
        style={{ width: '100%' }}
      >
        {'Add option'}
      </Button>
    </Wrapper>
  )
}

interface SortableItemProps {
  id: string,
  value: string,
  index: number,
  onChange: (value: string) => void,
  onRemove: () => void,
  allowEmptyValues: boolean
}

function SortableItem({ id, value, index, onChange, onRemove, allowEmptyValues }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  const showError = !allowEmptyValues && !value.trim()

  return (
    <ItemContainer ref={setNodeRef} style={style}>
      <ItemRow>
        <Button
          type={'text'}
          icon={<MenuOutlined />}
          size={'small'}
          style={{ cursor: 'grab' }}
          {...attributes}
          {...listeners}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Option ${index + 1}`}
          style={{ flex: 1, minWidth: 200 }}
          // @ts-expect-error - Success is not officially supported but it will override false negatives
          status={showError ? 'error' : 'success'}
        />
        <Button
          type={'text'}
          danger
          icon={<DeleteOutlined />}
          onClick={onRemove}
          size={'small'}
        />
      </ItemRow>
      {showError && (
        <ErrorMessage>{'This field is required'}</ErrorMessage>
      )}
    </ItemContainer>
  )
}

const Wrapper = styled.div(({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.margins.halfBaseMargin}px;
`)

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ItemRow = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.margins.halfBaseMargin}px;
`)

const ErrorMessage = styled.div(({ theme }) => `
  color: ${theme.colors.danger};
  font-size: 14px;
  margin-top: ${theme.margins.halfBaseMargin}px;
  margin-left: ${theme.margins.largeMargin}px;
`)
